/**
 * Customer Active List Component
 * Displays active customers with expandable service details and bulk CS assignment
 */
import { useState, useMemo, useCallback } from 'react'
import { UserPlus } from 'lucide-react'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import type { DataTableSelectionAction } from '@/components/ui/data-table-customized/types/selection'
import { useModal } from '@/contexts/modal'
import type { CustomerActive } from '@/modules/Customer/types'
import type { BulkAssignCSModalProps } from '@/modals/CustomerModals/BulkAssignCS'
import {
    useCustomerActiveList,
    getCustomerServices,
    hasServices,
} from '@/modules/Customer/hooks/use-customer-active-list'
import usePermissionsStore from '@/stores/usePermissionsStore'

const Active = () => {
    const { setModal } = useModal()
    const { canViewLeadOriginCustomers } = usePermissionsStore()
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    const {
        customers,
        pagination,
        stateComponent,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        serviceColumns,
        filterItems,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        t,
    } = useCustomerActiveList()

    // Bulk assign CS handler
    const handleBulkAssignCS = useCallback(
        (selectedRows: CustomerActive[]) => {
            setModal<BulkAssignCSModalProps>(
                'CustomerModals/BulkAssignCS/index',
                {
                    customers: selectedRows,
                    onSuccess: () => setSelectedKeys([]),
                }
            )
        },
        [setModal]
    )

    // Selection actions
    const selectionActions = useMemo<
        DataTableSelectionAction<CustomerActive>[]
    >(
        () => [
            {
                key: 'bulk-assign-cs',
                label: t('bulk_assign_cs', { count: selectedKeys.length }),
                icon: <UserPlus className="w-4 h-4" />,
                variant: 'info',
                onClick: handleBulkAssignCS,
                // Marketing (chỉ xem KH từ lead) không được gán CS hàng loạt
                hidden: () => canViewLeadOriginCustomers,
            },
        ],
        [t, selectedKeys.length, handleBulkAssignCS, canViewLeadOriginCustomers]
    )

    return (
        <Paper className="space-y-2 my-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
            />
            {stateComponent || (
                <DataTableCustomized<CustomerActive>
                    columns={columns}
                    rows={customers}
                    rowKey={(row) => row.customer_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_customers'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'customer-active-list',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    selection={{
                        selectedKeys,
                        onSelectionChange: setSelectedKeys,
                        getRowKey: (row) => String(row.customer_id),
                        // Chọn dòng chỉ để bulk assign CS; Marketing không có thao tác đó
                        canSelect: (row) =>
                            !row.has_cs && !canViewLeadOriginCustomers,
                        disabledReason: (row) =>
                            row.has_cs ? t('already_has_cs') : undefined,
                        actions: selectionActions,
                    }}
                    expandable={{
                        expandedColumns: serviceColumns,
                        expandedRowData: getCustomerServices,
                        expandedRowKey: (service) => String(service.service_id),
                        rowExpandable: hasServices,
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default Active
